import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

/**
 * Voice chatbot support: Deepgram text-to-speech for spoken AI Helper answers.
 *
 * Speech-to-text reuses `transcribeVoice` (Deepgram Nova-3) from dar.functions.
 * Answers still come from `aiOrchestrator`, so the spoken reply is grounded in
 * the caller's own workspace data and role-scoped tools.
 */

const VOICES = {
  thalia: "aura-2-thalia-en",
  orion: "aura-2-orion-en",
  luna: "aura-2-luna-en",
} as const;

export type VoiceKey = keyof typeof VOICES;

/** Strip markdown so the voice doesn't read asterisks, pipes and bullets aloud. */
function toSpeakable(input: string): string {
  return input
    .replace(/```[\s\S]*?```/g, " (code block omitted) ")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/^\s*#{1,6}\s*/gm, "")
    .replace(/^\s*[-*+]\s+/gm, "")
    .replace(/^\s*\|.*\|\s*$/gm, "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/[*_>#]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

export const speakText = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z
      .object({
        text: z.string().min(1).max(8000),
        voice: z.enum(["thalia", "orion", "luna"]).default("thalia"),
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    const key = process.env["DEEPGRAM_API_KEY"];
    if (!key) return { ok: false as const, error: "tts_not_configured" as const };

    const speakable = toSpeakable(data.text).slice(0, 2000);
    if (!speakable) return { ok: false as const, error: "nothing_to_speak" as const };

    const url = `https://api.deepgram.com/v1/speak?model=${VOICES[data.voice]}&encoding=mp3`;
    const res = await fetch(url, {
      method: "POST",
      headers: { Authorization: `Token ${key}`, "Content-Type": "application/json" },
      body: JSON.stringify({ text: speakable }),
    });

    if (!res.ok) {
      const detail = await res.text().catch(() => "");
      console.error("[voice-chat] deepgram speak failed", res.status, detail.slice(0, 300));
      if (res.status === 429) return { ok: false as const, error: "rate_limited" as const };
      return { ok: false as const, error: "tts_failed" as const };
    }

    const buf = new Uint8Array(await res.arrayBuffer());
    let binary = "";
    const chunk = 0x8000;
    for (let i = 0; i < buf.length; i += chunk) {
      binary += String.fromCharCode(...buf.subarray(i, i + chunk));
    }
    return {
      ok: true as const,
      mimeType: "audio/mpeg",
      audioBase64: btoa(binary),
      spoken: speakable,
    };
  });
